import {inject, Injectable} from './di';
import {ConfigService} from './config/config-service';
import {Logger} from './logger';

/**
 * A special service for accessing localStorage. Not all platforms have LocalStorage available, or available in full.
 * So any interactions with it must be done through this service.
 */
@Injectable()
export class StorageService {
  private readonly configService = inject(ConfigService);
  private readonly logger = inject(Logger);

  protected get ls(): LocalStorageProvider {
    return (
      this.configService.config.storage?.localStorage ||
      window.storage ||
      window.androidStorage ||
      localStorage
    );
  }

  /**
   * Get string stored on localStorage
   * @param path
   */
  public getString(path: string): string | undefined {
    try {
      if (!path) {
        this.logger.warn(`[StorageService] Attempt to get value by empty key. Most likely, this is a bug.`);
        return undefined;
      }

      const key = (this.configService.config.storage?.localStoragePrefix || '') + path;
      const text: string | undefined | null = this.ls.getItem(key);

      if (text == null || text === 'undefined' || text === 'null') {
        // NOTE: the string "undefined" is returned sometimes on some older browsers.
        return undefined;
      } else {
        return text;
      }
    } catch (err) {
      this.logger.error(`[StorageService] Unable to get value by key "${path}"`, err);
      return undefined;
    }
  }

  /**
   * Get number stored on localStorage
   * @param path
   */
  public getNumber(path: string): number | undefined {
    try {
      const str = this.getString(path);
      if (!str) {
        return undefined;
      } else {
        try {
          return Number(str);
        } catch (err) {
          this.logger.error(`[StorageService] Unable to parse number from string "${str}" stored in "${path}"`, err);
          return undefined;
        }
      }
    } catch (err) {
      this.logger.error(`[StorageService] Unable to get value by key "${path}"`, err);
      return undefined;
    }
  }

  /**
   * Get object stored in localStorage.
   * Please note that the value "null" will be treated as "undefined".
   * @param path
   */
  public getJson<T extends object>(path: string): T | undefined {
    try {
      const str = this.getString(path);
      if (!str) {
        return undefined;
      } else {
        try {
          return <T>JSON.parse(str);
        } catch (err) {
          this.logger.debug(`[StorageService] Unable to parse string value as JSON: ${str}`, err);
          return undefined;
        }
      }
    } catch (err) {
      this.logger.error(`[StorageService] Unable to get value by key "${path}"`, err);
      return undefined;
    }
  }

  /**
   * Get a list of keys stored in localStorage.
   */
  public getKeys(): string[] {
    try {
      return Object.keys(this.ls).filter((key) =>
        key.startsWith(this.configService.config.storage?.localStoragePrefix || ''),
      );
    } catch (err) {
      this.logger.error(`[StorageService] Unable to list storage keys`, err);
      return [];
    }
  }

  /**
   * Save value in localStorage
   * @param path key
   * @param data value to save
   */
  public set(path: string, data: string | number | boolean | object | null | undefined): void {
    try {
      if (typeof data === 'undefined') {
        this.logger.warn(
          `[StorageService] Attempt to save undefined value by key "${path}". Most likely, this is a bug.`,
        );
        return;
      }
      if (!path) {
        this.logger.warn(`[StorageService] Attempt to save value by empty key. Most likely, this is a bug.`);
        return;
      }

      let dataStr: string;
      if (typeof data === 'string' || typeof data === 'boolean' || typeof data === 'number') {
        dataStr = data.toString();
      } else {
        dataStr = JSON.stringify(data);
      }

      const key = (this.configService.config?.storage?.localStoragePrefix || '') + path;
      this.ls.setItem(key, dataStr);
      this.logger.debug(`[StorageService] Saving value with key "${path}"`);
    } catch (err) {
      this.logger.error(`[StorageService] Unable to set value using key "${path}"`, err);
    }
  }

  /**
   * Remove entry from localStorage
   * @param path key
   */
  public remove(path: string): void {
    try {
      if (!path) {
        this.logger.warn(`[StorageService] Attempt to remove value by empty key. Most likely, this is a bug.`);
        return;
      }
      this.ls.removeItem((this.configService.config.storage?.localStoragePrefix || '') + path);
      this.logger.debug(`[StorageService] Removing value with key "${path}"`);
    } catch (err) {
      this.logger.error(`[StorageService] Unable to remove value using key "${path}"`, err);
    }
  }

  /**
   * Clear all localStorage entries
   */
  public clear(): void {
    try {
      this.ls.clear();
      this.logger.debug(`[StorageService] localStorage cleared`);
    } catch (err) {
      this.logger.error(`[StorageService] Unable to clear storage`, err);
    }
  }
}

/**
 * Custom localStorage realization
 */
export interface LocalStorageProvider extends Storage {
  /**
   * Returns the current value associated with the given key, or null if the given key does not exist in the list
   * associated with the object.
   */
  getItem(key: string): string | null;

  /**
   * Removes the key/value pair with the given key from the list associated with the object, if a key/value pair with
   * the given key exists.
   */
  removeItem(key: string): void;

  /**
   * Sets the value of the pair identified by key to value, creating a new key/value pair if none existed for key
   * previously.
   *
   * Throws a "QuotaExceededError" DOMException exception if the new value couldn't be set. (Setting could fail if,
   * e.g., the user has disabled storage for the site, or if the quota has been exceeded.)
   */
  setItem(key: string, value: string): void;

  /**
   * Empties the list associated with the object of all key/value pairs, if there are any.
   */
  clear(): void;
}
